import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Link2, UserPlus, Wallet, ShieldCheck } from "lucide-react";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { affApi, affToken } from "../../lib/affApi";
import { fmtErr } from "../../lib/api";

const STEPS = [
  { icon: Link2, title: "Share your link", text: "Get a personal referral link the moment you sign up. Send it to owners, captains, managers — anyone running a yacht." },
  { icon: UserPlus, title: "Customer subscribes", text: "They create a Seavanto account through your link and add their yacht. Nothing changes for them — same price, same product." },
  { icon: Wallet, title: "You earn", text: "A percentage of every subscription fee they pay in their first 12 months lands in your balance. Paid out monthly by bank transfer." },
];

const FAQ = [
  ["Who can join?", "Brokers, captains, marina staff, yacht managers, content creators — anyone with access to yacht owners. Sign-up is free and instant."],
  ["How long do I earn for?", "12 months from the referred customer's first paid month. Every yacht the customer adds counts toward you."],
  ["When am I paid?", "On the 1st of each month, once your open balance passes the minimum. Smaller balances roll over to the next month."],
  ["Can I see who signed up?", "No — customer identities are protected. Your dashboard shows anonymised customers with spend, status and commission only."],
  ["Does the customer get a discount?", "No. Referred customers pay the standard rate; your commission comes out of Seavanto's share."],
];

function SignupForm({ program }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", promo_plan: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const { data } = await affApi.post("/affiliate/register", form);
      nav(`/affiliate/verify?email=${encodeURIComponent(data.email)}`);
    } catch (e2) { setErr(fmtErr(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 shadow-sm space-y-4" data-testid="aff-signup-form" id="signup">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase font-semibold text-emerald-700">Join free</div>
        <h2 className="font-display text-2xl mt-1">Become a Seavanto partner</h2>
        <p className="text-sm text-zinc-600 mt-1">Your referral link is ready the second you verify your email.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label htmlFor="aff-name">Full name</Label><Input id="aff-name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="aff-signup-name" /></div>
        <div><Label htmlFor="aff-company">Company (optional)</Label><Input id="aff-company" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="aff-signup-company" /></div>
        <div className="sm:col-span-2"><Label htmlFor="aff-email">Email</Label><Input id="aff-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="aff-signup-email" /></div>
        <div className="sm:col-span-2"><Label htmlFor="aff-pwd">Password</Label><Input id="aff-pwd" type="password" minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="aff-signup-password" /></div>
        <div className="sm:col-span-2">
          <Label htmlFor="aff-plan">How will you promote Seavanto? (optional)</Label>
          <Input id="aff-plan" placeholder="e.g. brokerage clients, captain network, newsletter" value={form.promo_plan} onChange={(e) => set("promo_plan", e.target.value)} data-testid="aff-signup-plan" />
        </div>
      </div>
      {err && <div className="text-sm text-red-600" data-testid="aff-signup-error">{err}</div>}
      <Button type="submit" disabled={busy} className="w-full bg-ink hover:bg-black" data-testid="aff-signup-submit">
        {busy ? "Creating…" : "Create partner account"} <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
      <p className="text-xs text-zinc-500 text-center">
        Already a partner? <Link to="/affiliate/login" className="underline text-ink" data-testid="aff-link-login">Sign in</Link>
        {program && <> · {program.rate_pct}% commission · {program.commission_months} months</>}
      </p>
    </form>
  );
}

export default function AffiliateLanding() {
  const nav = useNavigate();
  const [program, setProgram] = useState(null);
  useEffect(() => {
    if (affToken.get()) { nav("/affiliate/dashboard", { replace: true }); return; }
    affApi.get("/affiliate/public/program").then(({ data }) => setProgram(data)).catch(() => {});
  }, [nav]);
  const pct = program?.rate_pct ?? 20;
  return (
    <div className="bg-white text-ink" data-testid="affiliate-landing">
      <MarketingNav />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-1 text-xs text-zinc-700 mb-8">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Seavanto Affiliate Program
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tightest leading-[1.04] font-medium text-balance">
            Know yacht owners?<br /><span className="text-zinc-400">Earn {pct}% of every</span><br />subscription you bring in.
          </h1>
          <p className="mt-7 text-zinc-600 text-lg max-w-xl leading-relaxed">
            Share a personal link. Every yacht account that signs up through it pays you {pct}% of its Seavanto fees for the first {program?.commission_months ?? 12} months — monthly, by bank transfer.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.title} className="border-t border-zinc-200 pt-5" data-testid={`aff-step-${i + 1}`}>
                <div className="flex items-center gap-2 text-emerald-700"><s.icon className="h-4 w-4" /><span className="text-xs font-semibold tracking-widest uppercase">Step {i + 1}</span></div>
                <div className="font-display text-lg mt-2">{s.title}</div>
                <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5"><SignupForm program={program} /></div>
      </section>

      <section className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="aff-terms">
          {[[`${pct}%`, "of subscription fees, before tax"], [`${program?.commission_months ?? 12} months`, "commission window per customer"], ["Monthly", "payouts on the 1st, by bank transfer"], [`$${program?.min_payout ?? 100}`, "minimum payout — smaller balances roll over"]].map(([v, l]) => (
            <div key={l}><div className="font-display text-3xl lg:text-4xl">{v}</div><div className="text-sm text-zinc-400 mt-2">{l}</div></div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-2 text-emerald-700"><ShieldCheck className="h-4 w-4" /><span className="text-xs font-semibold tracking-widest uppercase">Good to know</span></div>
          <h2 className="font-display text-2xl mt-2">Questions partners ask</h2>
        </div>
        <dl className="lg:col-span-8 divide-y divide-zinc-200" data-testid="aff-faq">
          {FAQ.map(([q, a]) => (
            <div key={q} className="py-5 grid sm:grid-cols-3 gap-2"><dt className="font-medium">{q}</dt><dd className="sm:col-span-2 text-sm text-zinc-600 leading-relaxed">{a}</dd></div>
          ))}
        </dl>
      </section>
      <MarketingFooter />
    </div>
  );
}
